<template>
    <div style="display: inline-block" class="dropzone-wrapper-wrapper">
        <fullscreen-loading v-if="loading && foundDuplicate"/>
        <div class="dropzone-wrapper" style="display: inline-block" v-show="!foundDuplicate">
            <dropzone ref="uploadDropzone" :id="'id_' + unique_number_per_dropzone+'_dz-attachment'"
                      v-if="!resettingDropzone"
                      :name="'id_' + unique_number_per_dropzone+'_dz-attachment'"
                      @vdropzone-success="dropzoneSuccess"
                      @vdropzone-error="dropzoneError"
                      @vdropzone-canceled="dropzoneCanceled"
                      @vdropzone-removed-file="dropzoneRemovedFile"
                      @vdropzone-file-added="dropzoneAddedFile"
                      @vdropzone-reset="reset"
                      @vdropzone-files-added="filesAdded"
                      @vdropzone-total-upload-progress="totaluploadprogress"
                      @vdropzone-processing="processing"
                      @vdropzone-queue-complete="queueComplete"
                      :class="iconOnly ? 'dropzone-icon-only' : null"
                      :duplicateCheck="true"
                      :useCustomSlot="true"
                      v-tooltip.right-start="iconOnly ? language.dictDefaultMessage : null"
                      :options="{
                    url: getUrl(),
                    uploadMultiple: max_files > 1, // becomes array in upload  if true
                    parallelUploads: 1, // do not raise this without handling this new situation in CustomField::uploadedAttachment(files), the dropzoneSuccess event will be triggered for each file but in the parameters ALL files are present (needs filtering)
                    maxFiles: max_files, // total possible files in 1 dropzone
                    maxFilesize: max_size_mb_per_file, // max size PER FILE e.g. 10 MB
                    acceptedFiles: extensions_text,
                    addRemoveLinks: true,
                    dictDefaultMessage: language.dictDefaultMessage,
                    dictRemoveFile: language.dictRemoveFile,
                    dictResponseError: language.dictResponseError,
                    dictFileTooBig: language.dictFileTooBig,
                    dictMaxFilesExceeded: language.dictMaxFilesExceeded,
                    dictInvalidFileType: language.dictInvalidFileType,
                    headers: csrfHeader,
                    chunking: chunking,
                    forceChunking: chunking,
                    parallelChunkUploads: false,
                    chunkSize: 5000000,
                    retryChunks: true,
                    retryChunksLimit: 3,
                    timeout: timeout,
                    chunksUploaded: mergeChunks
                  }"
            >
                <input type="hidden" name="csrf-token" :value="csrfToken">
                <div class="dz-message needsclick">
                    <div class="margin-bottom-1" :class="!iconOnly ? '' : null">
                        <div style="color: #273444">
                            <div style="font-size:12px;">{{ icon_text }}</div>
                            <div style="display: block">
                                <icon name="cloud-upload-alt"></icon>
                            </div>
                        </div>
                    </div>
                    <span class="font-size-18" v-if="!iconOnly">{{ language.dictDefaultMessage }}</span>
                </div>
            </dropzone>

            <div v-if="loading && iconOnly" class="dropzone-loading">
                <span style="position: absolute;font-weight: 600" v-if="max_files > 1">{{ queued_files }}/{{ accepted_files }}</span>
                <span style="position: absolute;font-weight: 600" v-if="max_files <= 1">{{ chunkPercentage }}%</span>
            </div>
            <div v-if="iconOnly && file_errors && Object.values(file_errors).length" class="invalid-feedback">{{ Object.values(file_errors)[0] }}</div>
        </div>
        <div v-if="ceremonyCode && songs && foundDuplicate" class="mt-4">
            <h5>{{ $t('songs.duplicate_title') }}</h5>
            <p>{{ $t('songs.duplicate_message') }}</p>
            <button class="btn btn-secondary btn-sm" @click="uploadSelectedSong" :disabled="loading">{{ $t('songs.use_uploaded_song') }}</button>
            <song-library :predefined-songs="songs" :ceremony-code="ceremonyCode" @song_added="useLibrarySong"></song-library>
        </div>

        <!--for backend error messages? only TOO LARGE is implemented?-->
        <!--        <ul class="list-unstyled text-danger">-->
        <!--            <li v-for="(error, file) in file_errors">-->
        <!--                {{ file }}: {{ error }}-->
        <!--            </li>-->
        <!--        </ul>-->

        <!--        <span class="text-error" :data-error-input="getName()" v-show="verrors.has(getName())">{{ verrors.first(getName()) }}</span>-->

        <!--    DEBUG:    <button @click="$root.validateField(getName())">VEEV</button>-->

    </div>
</template>

<script>

    import Dropzone from 'vue2-dropzone';
    import {mapState} from "vuex";
    import FullscreenLoading from "./FullscreenLoading.vue";

    export default {

        mixins: [],

        components: {
            FullscreenLoading,
            Dropzone,
        },

        computed: mapState({
            token: state => state.token,
        }),

        props: {
            url: { // url, e.g. /account/uren/file_ingest
                type: String,
                required: true,
            },
            extensions_text: { // text on the dropzone for the extensions e.g. '.pdf,.png,.jpg,.jpeg,.gif'
                type: String,
                required: true,
            },
            max_files: { // total possible files in 1 dropzone
                type: Number | String, // string from <input type=number>
                required: true,
            },
            max_size_mb_per_file: { // max size PER FILE e.g. 10 MB
                type: Number | String,
                required: true,
            },
            bestand: { // #todo make a trans name temp NL because of feedback, only set when a valid file is uploaded
                type: Array,
                required: true,
            },
            unique_number_per_dropzone: { // pass e.g. option order in so the dropzone id wont clash
                type: Number,
                required: true,
            },
            required: {
                type: Boolean,
                required: false,
                default: true,
            },
            is_modal_open: { // used to indicate we want an emit for the file cleared (modal closed means auto destroy means false positive for clear file)
                type: Boolean,
                required: false,
                default: null,
            },
            validate_toggle: { // used to force a v-validate on the bestand prop
                type: Boolean,
                required: false,
                default: false,
            },
            empty_dropzone: {
                type: Number,
                required: false,
                default: 0
            },
            iconOnly: {
                type: Boolean,
                required: false,
                default: false
            },
            icon_text: {
                type: String,
                required: false,
                default: ''
            },
            chunking: {
                type: Boolean,
                default: false
            },
            timeout: {
                type: Number,
                default: 30000
            },
            selectedBlock: {
                default: false
            }
        },

        data() {
            return {
                file_errors: {},

                language: {
                    dictDefaultMessage: this.getDefaultMessage(),
                    dictRemoveFile: this.$t('files.remove_file'),
                    dictResponseError: this.$t('files.response_error'),
                    dictFileTooBig: this.$t('files.file_too_big'),
                    dictInvalidFileType: this.$t('files.invalid_file_type'),
                    dictMaxFilesExceeded: this.$t('files.max_files_exceeded', { max_files: this.max_files }),
                },
                // re-enable
                csrfToken: document.head.querySelector('meta[name="csrf-token"]').content,
                csrfHeader: {
                    'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]').content
                },
                resettingDropzone: false,
                accepted_files: 0,
                queued_files: 0,

                loading: false,
                chunkFile: null,

                chunkPercentage: 0,
                ceremonyCode: null,
                songs: null,
                uploadedResponse: {
                    file: null, response: null
                },
                foundDuplicate: false,
            }
        },

        mounted() {
            this.$emit('mix_registerValidation', this.$validator);
            this.csrfHeader.Authorization = this.token ? ('Bearer ' + this.token) : null;
            this.ceremonyCode = this.$route.params.code !== 'create' ? this.$route.params.code : null;
        },

        watch: {
            empty_dropzone() {
                this.$refs.uploadDropzone.removeAllFiles(true);
            },

            validate_toggle() {
                console.log('WATCH DropzoneUpload::validate_toggle() bestand: ' + this.bestand);
            },

            bestand() {
                // console.log('WATCH bestand: ',this.bestand);
                if (this.bestand) {
                    // console.log('WATCH bestand: triggering reset!');
                    // this.resetDropzone();
                }
            },

            file_errors: {
                handler(value) {
                    // console.log('WATCH DropzoneUpload::file_errors()');
                    // this.$forceUpdate();
                },
                deep: true,
            },

            token() {
                this.csrfHeader.Authorization = this.token ? ('Bearer ' + this.token) : null;
            }
        },

        beforeDestroy() {
            this.$store.commit('stopUploading', this._uid);
        },

        methods: {
            getChunkPercentage() {
                if (!this.$refs.uploadDropzone) {
                    return 0;
                }

                const files = this.$refs.uploadDropzone.getUploadingFiles();

                if (files.length <= 0) {
                    return 0;
                }

                const upload = files[0].upload;

                if (!upload || !upload.chunks) {
                    return 0;
                }

                const totalChunks = upload.totalChunkCount;
                const chunksUploaded = upload.chunks.filter(chunk => chunk.status === 'success').length;
                const progress = chunksUploaded / totalChunks * 100;

                return progress >= 100 ? 100 : parseInt(progress);
            },

            getUrl() {
                return this.url + (this.chunking ? '/chunk' : '');
            },

            async mergeChunks(file, done) {
                try {
                    const response = await this.$http.post(this.getUrl() + '/' + file.upload.uuid + '/commit', {
                        uuid: file.upload.uuid,
                        name: file.name,
                        expected_bytes: file.size,
                        total_chunks: file.upload.totalChunkCount
                    });

                    this.$emit('chunks_merged', response.data)

                    done();
                } catch (exception) {
                    let message = this.$t('files.upload_error');

                    if (exception.response && exception.response.data && exception.response.data.message) {
                        message = exception.response.data.message;
                    }

                    file.accepted = false;
                    this.$refs.uploadDropzone.dropzone._errorProcessing([file], message);
                    this.$set(this.file_errors, file.name, message);
                }
            },

            async removeChunks(file) {
                try {
                    await this.$http.delete(this.getUrl() + '/' + file.upload.uuid + '/clear');
                } catch (exception) {
                    console.error('Cannot remove chunks');
                }
            },

            filesAdded() {
                console.log('Files added');

                this.accepted_files = this.$refs.uploadDropzone.getAcceptedFiles().length;
                this.$store.commit('startUploading', this._uid);
                this.$emit('files-added');
            },

            processing(data) {
                let queuedfiles = this.$refs.uploadDropzone.getQueuedFiles().length;

                const totalQueuedFiles = this.accepted_files - queuedfiles
                this.queued_files = totalQueuedFiles <= 0 ? 0 : totalQueuedFiles;

                this.loading = true;
            },

            totaluploadprogress(data, progress) {
                this.chunkPercentage = this.getChunkPercentage();
                // console.log('totaluploadprogress', data, progress)
            },
            //
            // uploadprogress(data, progress) {
            //     console.log('uploadprogress', data, progress)
            // },

            queueComplete(e) {
                // buggy dropzone triggers a queuecomplete when seeing an error. even when there are more to upload.
                if (this.$refs.uploadDropzone.getUploadingFiles().length === 0 && this.$refs.uploadDropzone.getQueuedFiles().length === 0 && !this.foundDuplicate) {
                    this.$emit('queueComplete', this.$refs.uploadDropzone.getRejectedFiles().length <= 0 ? 'success' : 'failed');
                    this.resetDropzone();
                }

                this.loading = false;
                this.$store.commit('stopUploading', this._uid);
            },

            reset() {
                console.log('reset');
                // this.$emit('clear_dropzone_errors');
                this.loading = false;
            },

            updateDropzoneUrl() {
                this.$refs.uploadDropzone.setOption('url', this.url);
            },

            dropzoneSuccess(file, response) {
                this.$emit('uploaded', file, response);
                this.$refs.uploadDropzone.removeFile(file);
            },

            async uploadSelectedSong() {
                this.loading = true;
                try {
                    let formData = new FormData();
                    formData.append('file', this.uploadedResponse.file);
                    formData.append('allow_duplicates', true);
                    const response = await this.$http.post(`/api/ceremonies/code/${this.ceremonyCode}/files/${this.selectedBlock.id}`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    this.dropzoneSuccess(this.uploadedResponse.file, response.data);
                    this.$emit('queueComplete', this.$refs.uploadDropzone.getRejectedFiles().length <= 0 ? 'success' : 'failed');
                } catch (e) {
                    console.log(e)
                } finally {
                    this.loading = false;
                }
            },

            useLibrarySong(song) {
                this.$emit('song_added', song);
                this.$refs.uploadDropzone.removeFile(this.uploadedResponse.file);
                this.$emit('queueComplete', this.$refs.uploadDropzone.getRejectedFiles().length <= 0 ? 'success' : 'failed');
            },

            dropzoneCanceled(file) {
                console.log('dropzone cancelled');

                this.dropzoneError(file, {
                    message: this.$t('files.upload_timeout'),
                });
            },

            async dropzoneError(file, error) {
                if (this.chunking) {
                    this.removeChunks(file);
                }

                if (this.iconOnly) {
                    // this.resetDropzone();
                }

                this.file_errors = {};
                if (file.xhr && file.xhr.status === 413 /* too large */) {
                    this.$set(this.file_errors, file.name, this.$t('files.file_too_large', { size: this.max_size_mb_per_file }));
                    return;
                }
                // this can also be a filesize error. But triggered by the dropzone limit instead of php
                this.$set(this.file_errors, file.name, error.message || error);

                console.log('emitting errors', error);

                // if duplicate then give the choice to use the library or own uploaded song
                if (error.type === 'duplicate error') {
                    this.foundDuplicate = true;
                    this.songs = error.duplicate_songs;
                    this.uploadedResponse = {
                        file: file,
                        response: error
                    }
                } else {
                    this.$emit('dropzone_errors', {
                        name: file.name, error: error.message || error
                    });
                }
            },

            dropzoneRemovedFile: function (file, error, xhr, event) {

                // console.log('DropzoneUpload::dropzoneRemovedFile() removing filename: ' + file.name + ', is_modal_open: ' + this.is_modal_open);

                //#todo this is made on single files, support multiple

                // only emit when the user clears the file, not on auto-destroy very dangerous closing a modal on save will clear the file :-(
                if (this.is_modal_open === null /* prop not used */ || this.is_modal_open === true /* not auto-destroy */) {
                    // console.log('DropzoneUpload::dropzoneRemovedFile() emit clear_file');
                    this.$emit('clear_file', file);
                }
            },

            dropzoneAddedFile: function (file) {
                // console.log('DropzoneUpload::dropzoneAddedFile() removing filename: ' + file.name + ', is_modal_open: ' + this.is_modal_open);

                var ext = file.name.split('.').pop();
                // console.log('DropzoneUpload::dropzoneAddedFile() ext: ' + ext);

                // rofl Yeequery
                if (ext == "pdf") {
                    $(file.previewElement).find(".dz-image img").attr("src", "/images/thumb_pdf.png");
                } else if (ext.indexOf("doc") != -1) {
                    $(file.previewElement).find(".dz-image img").attr("src", "/images/thumb_word.png");
                } else if (ext.indexOf("xls") != -1) {
                    $(file.previewElement).find(".dz-image img").attr("src", "/images/thumb_excel.png");
                }

                this.$emit('file_added', file);
            },

            getValidations() {
                if (this.required) {
                    return 'required';
                }
                return '';
            },

            resetDropzone() {
                // console.log('Resetting dropzone..');
                this.resettingDropzone = true;
                setTimeout(() => {
                    // console.log('Reloading  dropzone..');
                    this.resettingDropzone = false;
                }, 10);
            },

            getDefaultMessage() {
                if (this.max_files > 1) {
                    return this.$t('files.multiple_files_message', {
                        max_files: this.max_files,
                        extensions: this.extensions_text,
                        max_size: this.max_size_mb_per_file
                    });
                }

                // 1 file
                return this.$t('files.single_file_message', {
                    extensions: this.extensions_text,
                    max_size: this.max_size_mb_per_file
                });
            },

        }

    }

</script>

<style lang="scss">

    @import '../../../../node_modules/dropzone/dist/dropzone.css';


    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .dropzone-icon-only {
        min-height: 0;
        width: 115px;
        height: 115px;
        padding: 0;

        &,
        .dz-message,
        .dz-message > * {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .fa-cloud-upload-alt {
            font-size: 40px;
            cursor: pointer;
        }

        .dz-preview {
            display: none !important;
        }
    }

    .dropzone {
        border: 2px dotted rgba(0, 0, 0, 0.3);
    }

    .dropzone-wrapper {
        position: relative;

        .dropzone-loading {
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;

            &:after {
                content: '';
                border: 3px dotted #4a4a4a;
                border-top: 3px dotted transparent;
                border-radius: 50%;
                width: 80px;
                height: 80px;
                animation: spin 2s linear infinite;
                display: inline-block;
            }
        }
    }

    @media(max-width: 767px) {
        .dropzone-wrapper-wrapper {
            width: 100%;

            .dropzone-wrapper {
                width: 100%;

                .vue-dropzone {
                    width: 100%;
                }
            }
        }
    }

    //@keyframes spin {
    //    0% {
    //        transform: rotate(0deg);
    //    }
    //    100% {
    //        transform: rotate(360deg);
    //    }
    //}
    //
    //.dropzone-icon-only {
    //    min-height: 0;
    //    width: 70px;
    //    height: 70px;
    //    padding: 0;
    //
    //    &,
    //    .dz-message,
    //    .dz-message > * {
    //        display: flex;
    //        align-items: center;
    //        justify-content: center;
    //    }
    //
    //    .bxs-cloud-upload {
    //        font-size: 40px;
    //    }
    //
    //    .dz-preview {
    //        display: none !important;
    //    }
    //}
    //
    //.dropzone-wrapper {
    //    position: relative;
    //
    //    .dropzone-loading {
    //        width: 100%;
    //        height: 100%;
    //        position: absolute;
    //        top: 0;
    //        left: 0;
    //        display: flex;
    //        align-items: center;
    //        justify-content: center;
    //        pointer-events: none;
    //
    //        &:after {
    //            content: '';
    //            border: 2px solid #f8f8fb;
    //            border-top: 2px solid #556ee6;
    //            border-radius: 50%;
    //            width: 30px;
    //            height: 30px;
    //            animation: spin 2s linear infinite;
    //            display: inline-block;
    //        }
    //    }
    //}

</style>
